import React, { useContext } from "react";
import { createBrowserRouter, RouterProvider } from "react-router-dom";
import ProtectedRoute from "./components/child/ProtectedRoute";
import { UserContext } from "./components/context/userContext";
import Layout from "./components/child/Layout";
import Login from "./components/pages/authentication/Login";
import SignUp from "./components/pages/authentication/signUp";
import Profile from "./components/pages/Profile";
import NewPair from "./components/pages/settings/newPair";
import RolesList from "~/components/pages/settings/roles/list/index";
import CreateRole from "~/components/pages/settings/roles/createOrEdit";
import RoleDetails from "~/components/pages/settings/roles/details";
import NewAnalysis from "~/components/pages/heatMap/analysis/createNew/index";
import AnalysisList from "~/components/pages/heatMap/analysis/List";
import UserList from "~/components/pages/heatMap/userList";
import UserProfile from "~/components/pages/heatMap/userList/UserProfile";
import NotificationList from "~/components/pages/heatMap/notification/list";
import FundamentalView from "~/components/pages/heatMap/analysis/view/Fundamental";
import TechnicalView from "~/components/pages/heatMap/analysis/view/technical";
import OnchainView from "~/components/pages/heatMap/analysis/view/OnChain";
import HeatMapList from "~/components/pages/heatMap/heatMap/List";
import HeatMapView from "~/components/pages/heatMap/heatMap/view/View";
import Imbalances from "~/components/pages/tools/imbalances/Imbalances";
import FullChart from "~/components/pages/fullChart/FullChart";
import CreateOrEditSetup from "~/components/pages/setup/createOrEdit";
import SetupList from "~/components/pages/setup/List";
import SetupView from "~/components/pages/setup/view";
import ValumeMarketCap from "~/components/pages/tools/valumeMarketCap/createNew";
import TokensList from "~/components/pages/tokensList";
import "./assets/scss/styles.scss";
import "~/styles.css";


const App = () => {
  const { user, updateUser } = useContext(UserContext);
  const permissions = user?.role[0]?.permissions;

  const router = createBrowserRouter([
    { path: "/login", element: <Login /> },
    { path: "/signUp", element: <SignUp /> },

    {
      path: "/profile",
      element: (
        <ProtectedRoute>
          <Layout user={user} breadcrumbItems={{ main: "Profile" }}>
            <Profile user={user} updateUser={updateUser} />
          </Layout>
        </ProtectedRoute>
      ),
    },
    {
      path: "/",
      element: (
        <ProtectedRoute>
          <Layout
            user={user}
            breadcrumbItems={{
              main: "HeatMap",
              sub: ["Analysis", "New Analysis"],
            }}
          >
            <NewAnalysis user={user} permissions={permissions} />
          </Layout>
        </ProtectedRoute>
      ),
    },

    {
      path: "/analysisList",
      element: (
        <ProtectedRoute>
          <Layout
            user={user}
            breadcrumbItems={{
              main: "HeatMap",
              sub: ["Analysis List"],
            }}
          >
            <AnalysisList user={user} permissions={permissions} />
          </Layout>
        </ProtectedRoute>
      ),
    },

    {
      path: "/userList",
      element: (
        <ProtectedRoute>
          <Layout
            user={user}
            breadcrumbItems={{
              main: "HeatMap",
              sub: ["User List"],
            }}
          >
            <UserList />
          </Layout>
        </ProtectedRoute>
      ),
    },
    {
      path: "userList/userProfile",
      element: (
        <ProtectedRoute>
          <Layout
            user={user}
            breadcrumbItems={{
              main: "HeatMap",
              sub: ["User profile Detail"],
            }}
          >
            <UserProfile />
          </Layout>
        </ProtectedRoute>
      ),
    },
    {
      path: "/imbalances",
      element: (
        <ProtectedRoute>
          <Layout
            user={user}
            breadcrumbItems={{
              main: "Tools",
              sub: ["Imbalances"],
            }}
          >
            <Imbalances />
          </Layout>
        </ProtectedRoute>
      ),
    },
    {
      path: "/newPair",
      element: (
        <ProtectedRoute>
          <Layout
            user={user}
            breadcrumbItems={{
              main: "Settings",
              sub: ["New Pair"],
            }}
          >
            <NewPair />
          </Layout>
        </ProtectedRoute>
      ),
    },
    {
      path: "/roles",
      element: (
        <ProtectedRoute>
          <Layout
            user={user}
            breadcrumbItems={{
              main: "Settings",
              sub: ["Role List"],
            }}
          >
            <RolesList />
          </Layout>
        </ProtectedRoute>
      ),
    },
    {
      path: "/roles/newRole",
      element: (
        <ProtectedRoute>
          <Layout
            user={user}
            breadcrumbItems={{
              main: "Settings",
              sub: ["Roles", "Create new role"],
            }}
          >
            <CreateRole />
          </Layout>
        </ProtectedRoute>
      ),
    },
    {
      path: "/roles/editRole",
      element: (
        <ProtectedRoute>
          <Layout
            user={user}
            breadcrumbItems={{
              main: "Settings",
              sub: ["Roles", "Edit role"],
            }}
          >
            <CreateRole />
          </Layout>
        </ProtectedRoute>
      ),
    },
    {
      path: "/roles/:id",
      element: (
        <ProtectedRoute>
          <Layout
            user={user}
            breadcrumbItems={{
              main: "Settings",
              sub: ["Roles", "Role Detail"],
            }}
          >
            <RoleDetails />
          </Layout>
        </ProtectedRoute>
      ),
    },

    {
      path: "/onchain-view/:id",
      element: (
        <ProtectedRoute>
          <Layout
            user={user}
            breadcrumbItems={{
              main: "HeatMap",
              sub: ["Analysis", "Details"],
            }}
          >
            <OnchainView permissions={permissions} />
          </Layout>
        </ProtectedRoute>
      ),
    },
    {
      path: "/technical-view/:id",
      element: (
        <ProtectedRoute>
          <Layout
            user={user}
            breadcrumbItems={{
              main: "HeatMap",
              sub: ["Analysis", "Details"],
            }}
          >
            <TechnicalView user={user} permissions={permissions} />
          </Layout>
        </ProtectedRoute>
      ),
    },
    {
      path: "/fundamental-view/:id",
      element: (
        <ProtectedRoute>
          <Layout
            user={user}
            breadcrumbItems={{
              main: "HeatMap",
              sub: ["Analysis", "Details"],
            }}
          >
            <FundamentalView permissions={permissions} />
          </Layout>
        </ProtectedRoute>
      ),
    },

    {
      path: "/heatMap-list",
      element: (
        <ProtectedRoute>
          <Layout
            user={user}
            breadcrumbItems={{
              main: "HeatMap",
              sub: ["HeatMap List"],
            }}
          >
            <HeatMapList permissions={permissions} />
          </Layout>
        </ProtectedRoute>
      ),
    },

    {
      path: "/heatMap/:id",
      element: (
        <ProtectedRoute>
          <Layout
            user={user}
            breadcrumbItems={{
              main: "HeatMap",
              sub: ["HeatMap List", "Details"],
            }}
          >
            <HeatMapView permissions={permissions} />
          </Layout>
        </ProtectedRoute>
      ),
    },
    {
      path: "/newSetup",
      element: (
        <ProtectedRoute>
          <Layout
            user={user}
            breadcrumbItems={{
              main: "HeatMap",
              sub: ["New Setup"],
            }}
          >
            <CreateOrEditSetup permissions={permissions} />
          </Layout>
        </ProtectedRoute>
      ),
    },
    {
      path: "/editSetup",
      element: (
        <ProtectedRoute>
          <Layout
            user={user}
            breadcrumbItems={{
              main: "Setup List",
              sub: ["Edit Setup"],
            }}
          >
            <CreateOrEditSetup permissions={permissions} />
          </Layout>
        </ProtectedRoute>
      ),
    },
    {
      path: "/setupList",
      element: (
        <ProtectedRoute>
          <Layout
            user={user}
            breadcrumbItems={{
              main: "Setup List",
            }}
          >
            <SetupList permissions={permissions} />
          </Layout>
        </ProtectedRoute>
      ),
    },
    {
      path: "/setup",
      element: (
        <ProtectedRoute>
          <Layout
            user={user}
            breadcrumbItems={{
              main: "Setup List",
              sub: ["Details"],
            }}
          >
            <SetupView user={user} permissions={permissions} />
          </Layout>
        </ProtectedRoute>
      ),
    },
    {
      path: "/vmc",
      element: (
        <ProtectedRoute>
          <Layout
            user={user}
            breadcrumbItems={{
              main: "Tools",
              sub: ["Valume / Market Cap"],
            }}
          >
            <ValumeMarketCap />
          </Layout>
        </ProtectedRoute>
      ),
    },

    {
      path: "/coinList",
      element: (
        <ProtectedRoute>
          <Layout
            user={user}
            breadcrumbItems={{
              main: "Coin list",
            }}
          >
            <TokensList />
          </Layout>
        </ProtectedRoute>
      ),
    },

    {
      path: "/notification",
      element: (
        <ProtectedRoute>
          <Layout
            user={user}
            breadcrumbItems={{ main: "HeatMap", sub: ["Notification"] }}
          >
            <NotificationList permissions={permissions} />
          </Layout>
        </ProtectedRoute>
      ),
    },

    {
      path: "/fullchart",
      element: (
        <ProtectedRoute>
          <Layout
            user={user}
            breadcrumbItems={{
              main: "Chart",
            }}
          >
            <FullChart />
          </Layout>
        </ProtectedRoute>
      ),
    },
  ]);

  return <RouterProvider router={router} />;
};

export default App;
